/**
 * RENDER_FROM_TRUTH — pure identity/order helpers for the derived message list.
 *
 * Target model: the rendered list derives strictly from seq-ordered server
 * rows (`messages.seq`, DB insert order — global, never-null, monotonic per
 * task); streaming is a thin ephemeral overlay that never owns identity. The
 * overlay dies ONLY when its server row arrives in the committed list — never
 * on a lifecycle change (killing it on lifecycle was the "half output then
 * everything appeared" bug).
 *
 * Extracted verbatim from apps/web/src/stores/renderTruth.ts so every
 * conversation surface (web app, extension side panel) shares ONE identity
 * brain. Structurally typed on the minimal field set the logic reads, so the
 * web's store `Message` and a panel row both satisfy it without coupling this
 * module to either store's full shape.
 */
import type { MessageIdentity } from '../types/messages.js';

/** Minimal row surface the identity/order logic reads. */
export interface ConversationRow {
  id: string;
  seq?: number;
  sdkMessageUuid?: string;
  expectedServerId?: string;
  identity?: MessageIdentity;
}

/**
 * Order committed rows by per-row `seq`, keeping seq-less rows (optimistic
 * sends, context-lost/checkpoint markers) GLUED after the seq-bearing row
 * they currently follow. A seq-less row inherits its nearest preceding
 * anchor's seq and ties break by current position, so it can never jump
 * across rows the server has ordered (#1402 guarantee).
 */
export function orderBySeq<T extends { seq?: number }>(rows: T[]): T[] {
  const effective = new Array<number>(rows.length);
  let anchor = Number.NEGATIVE_INFINITY;
  rows.forEach((row, i) => {
    if (typeof row.seq === 'number') anchor = row.seq;
    effective[i] = anchor;
  });
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const ea = effective[a.index];
      const eb = effective[b.index];
      if (ea < eb) return -1;
      if (ea > eb) return 1;
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}

/**
 * True when the overlay's server row has arrived in the committed list —
 * matched by the AGENT_END-announced `expectedServerId` (against row id or
 * persisted identity.serverId) or by `sdkMessageUuid`. This is the ONLY
 * legal way the overlay dies.
 */
export function overlayDead(overlay: ConversationRow, committed: readonly ConversationRow[]): boolean {
  const expected = overlay.expectedServerId;
  const sdkUuid = overlay.sdkMessageUuid;
  if (!expected && !sdkUuid) return false;
  return committed.some((row) => {
    if (expected) {
      if (row.id === expected) return true;
      if (row.identity?.kind === 'persisted' && row.identity.serverId === expected) return true;
    }
    return Boolean(sdkUuid) && row.sdkMessageUuid === sdkUuid;
  });
}
